import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import Icon from "../icon/Icon";
import styles from "./file.module.css";

const FilePicker = ({
  onFileSelect,
  width,
  height,
  image,
  readOnly,
  accept,
}) => {
  const dragDropContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const openFileDialog = () => {
    if (!readOnly) {
      fileInputRef.current.click();
    }
  };

  const isVideo = (fileOrUrl) => {
    if (!fileOrUrl) return false;

    if (fileOrUrl instanceof File) {
      return fileOrUrl.type.startsWith("video/");
    }

    return /\.(mp4|webm|ogg)$/i.test(fileOrUrl);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (readOnly) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      onFileSelect(file);
    }
  };

  const onFileChange = (file) => {
    if (!file) return;
    setSelectedFile(file);
    onFileSelect(file);
  };

  const renderPreview = () => {
    // LOCAL FILE PREVIEW
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      return isVideo(selectedFile) ? (
        <video
          src={url}
          className={styles.previewMedia}
          controls
        />
      ) : (
        <img
          src={url}
          className={styles.previewMedia}
          alt="preview"
        />
      );
    }

    // SERVER FILE PREVIEW
    if (image) {
      const url = `${process.env.REACT_APP_S3_URL}/${image}`;
      return isVideo(image) ? (
        <video
          src={url}
          className={styles.previewMedia}
          controls
        />
      ) : (
        <img
          src={url}
          className={styles.previewMedia}
          alt="preview"
        />
      );
    }

    // EMPTY STATE
    return <Icon icon="camera-fill" size="3x" />;
  };

  return (
    <div
      ref={dragDropContainerRef}
      className={styles.container}
      style={{ width, height }}
      onClick={openFileDialog}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {renderPreview()}

      <input
        ref={fileInputRef}
        type="file"
        className="d-none"
        disabled={readOnly}
        accept={accept ?? ""}
        onChange={(e) => onFileChange(e.target.files[0])}
      />
    </div>
  );
};

FilePicker.propTypes = {
  onFileSelect: PropTypes.func.isRequired,
  width: PropTypes.string,
  height: PropTypes.string,
  image: PropTypes.string,
  readOnly: PropTypes.bool,
  accept: PropTypes.string,
};

FilePicker.defaultProps = {
  width: "auto",
  height: "100%",
  image: "",
  readOnly: false,
  accept: "image/*,video/*",
};

export default FilePicker;
